import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Alert, Button, Nav, NavItem, NavLink } from 'reactstrap';
import { State } from '../../redux/state';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization, withRouterAndHelpers, RouterProps } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import * as selectors from '../../redux/selectors';
import Footer from '../../shared/Footer';
import {
  NormalizedNotification,
  NotificationCategory,
  normalizeNotification,
  resolveNotificationPath,
  isNotificationUnread,
  notificationActionLabel,
  filterNotificationsByCategory
} from '../../utils/notificationUtils';
import './notifications.scss';

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
  isModerator: boolean;
}

interface LocalState {
  items: NormalizedNotification[];
  unreadCount: number;
  loading: boolean;
  error: string;
  activeCategory: NotificationCategory;
}

const CATEGORY_TABS: Array<{ id: NotificationCategory; labelKey: string }> = [
  { id: 'all', labelKey: 'All' },
  { id: 'comments', labelKey: 'Comments' },
  { id: 'likes', labelKey: 'Likes' },
  { id: 'flags', labelKey: 'Flags' },
  { id: 'hero', labelKey: 'Hero nominations' },
  { id: 'announcements', labelKey: 'Announcements' }
];

class NotificationsPage extends React.Component<LProps<RouterProps<{}> & Props>, LocalState> {
  public state: LocalState = {
    items: [],
    unreadCount: 0,
    loading: false,
    error: '',
    activeCategory: 'all'
  };

  public componentDidMount() {
    this.load();
  }

  private authHeaders = () => ({
    'Authorization': this.props.token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  })

  private load = async () => {
    const { apiBase, competitionName } = this.props;
    this.setState({ loading: true, error: '' });
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/notifications?pageSize=50`,
        { headers: this.authHeaders() }
      );
      if (!res.ok) {
        this.setState({ loading: false, error: 'Failed to load notifications.' });
        return;
      }
      const data = await res.json();
      const items = (data.notifications || []).map((raw: Record<string, unknown>) => normalizeNotification(raw));
      this.setState({
        items,
        unreadCount: data.unreadCount || 0,
        loading: false
      });
    } catch (_err) {
      this.setState({ loading: false, error: 'Failed to load notifications.' });
    }
  }

  private markAllRead = async () => {
    const { apiBase, competitionName } = this.props;
    await fetch(
      `${apiBase}/competitions/${encodeURIComponent(competitionName)}/notifications/read_all`,
      { method: 'POST', headers: this.authHeaders() }
    );
    this.setState({
      unreadCount: 0,
      items: this.state.items.map((item) => ({
        ...item,
        readAt: item.readAt || new Date().toISOString()
      }))
    });
  }

  private openItem = async (item: NormalizedNotification) => {
    const { apiBase, history, t } = this.props;
    if (isNotificationUnread(item)) {
      await fetch(`${apiBase}/notifications/${item.id}/read`, {
        method: 'POST',
        headers: this.authHeaders()
      });
      this.setState({
        unreadCount: Math.max(0, this.state.unreadCount - 1),
        items: this.state.items.map((n) =>
          n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n
        )
      });
    }
    history.push(resolveNotificationPath(item, t));
  }

  private setCategory = (category: NotificationCategory) => {
    this.setState({ activeCategory: category });
  }

  public render() {
    const { t, isModerator } = this.props;
    const { items, unreadCount, loading, error, activeCategory } = this.state;
    const visibleItems = filterNotificationsByCategory(items, activeCategory, isModerator);
    return (
      <div>
        <div className="container cc__notifications_page" style={{ maxWidth: 760, paddingTop: 24, paddingBottom: 40 }}>
          <div className="cc__notifications_page__header">
            <div>
              <h2>{t('Notifications')}</h2>
              <p className="cc__lighttext">
                {t('Likes, comments on your stories, thread replies, content reports, and hero nominations appear here. Reporter identity is never shown to participants.')}
              </p>
            </div>
            {unreadCount > 0 &&
              <Button color="link" size="sm" onClick={this.markAllRead}>{t('Mark all read')}</Button>
            }
          </div>
          <Nav tabs={true} className="cc__notifications_page__tabs">
            {CATEGORY_TABS.map((tab) =>
              <NavItem key={tab.id}>
                <NavLink
                  href="#"
                  active={activeCategory === tab.id}
                  onClick={(e) => { e.preventDefault(); this.setCategory(tab.id); }}
                >{t(tab.labelKey)}</NavLink>
              </NavItem>
            )}
          </Nav>
          {error && <Alert color="danger">{error}</Alert>}
          {loading && <div className="cc__lighttext">{t('Loading…')}</div>}
          {!loading && visibleItems.length === 0 &&
            <div className="cc__lighttext">{t('No notifications in this category yet.')}</div>
          }
          {!loading &&
            <div className="cc__notifications_page__list">
              {visibleItems.map((item) =>
                <div
                  key={item.id}
                  className={isNotificationUnread(item)
                    ? 'cc__notifications_page__item cc__notifications_page__item--unread'
                    : 'cc__notifications_page__item'}
                >
                  <div className="cc__notifications_page__item__content">
                    <div className="cc__notifications_page__title">{item.title}</div>
                    <div className="cc__notifications_page__body">{item.body}</div>
                    <div className="cc__notifications_page__meta">{item.createdAt}</div>
                  </div>
                  <Button color="link" size="sm" onClick={() => this.openItem(item)}>
                    {notificationActionLabel(item, t)}
                  </Button>
                </div>
              )}
            </div>
          }
        </div>
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state: State): Props => ({
  apiBase: userApiBasePath(state),
  token: state.token || '',
  competitionName: (state.competition && state.competition.name) || selectors.competitionName(state) || '',
  isModerator: selectors.canViewCommsTools(state)
});

export default asPage({ requiresCompetitionStart: false, authenticated: true })(compose(
  withLocalization('navigation'),
  withRouterAndHelpers,
  connect(mapStateToProps)
)(NotificationsPage));
